"use strict";
/**
 * 状态
 *                        | -> stop
 *                  |-> close -> reconnect
 * '' -> connecting -> open          |
 *                       ^-----------|
 */
Object.defineProperty(exports, "__esModule", { value: true });
var EventEmitter = require("eventemitter3");
exports.EventEmitter = EventEmitter;
var utils_1 = require("./utils");
var socket_1 = require("./socket");
var ReconnectStrategy;
(function (ReconnectStrategy) {
    ReconnectStrategy[ReconnectStrategy["Normal"] = 0] = "Normal";
    ReconnectStrategy[ReconnectStrategy["Stop"] = 1] = "Stop";
    ReconnectStrategy[ReconnectStrategy["Immediate"] = 2] = "Immediate";
})(ReconnectStrategy = exports.ReconnectStrategy || (exports.ReconnectStrategy = {}));
exports.baseOption = {
    /**
     * proto未设置的情况下，在android 5.x下面会发sec-websocket-protocol:undefined的request头，如果后端没处理，会报错。
     * 如果传`[]`则没问题。
     */
    proto: [],
    /** 最大尝试重连次数，默认值为 10 次 */
    maxRetryAttempts: 10,
    /** 登录超时时间 */
    loginTimeout: 10000,
    /** 心跳间隔 */
    beatInterval: 30000,
    /** 心跳超时时间 */
    beatTimeout: 20000,
    /** 最大心跳超时次数 **/
    maxBeatTimeoutTimes: 2,
    /** 断开连接后的重连间隔 */
    tryInterval: 2000,
    /** 最大的重连间隔 */
    maxTryInterval: 30000,
    /** 重连间隔加速度 */
    tryAccelerate: 2,
    /** 连接超时间隔 */
    connectTimeout: 2000,
    mapRequestId: function (value) { return value.requestId; },
    mapResult: function (value) { return value.body.code === 200; },
    socketProvider: socket_1.SocketProvider.DOM
};
var BaseClient = /** @class */ (function () {
    function BaseClient(userOption) {
        var _this = this;
        if (userOption === void 0) { userOption = {}; }
        this.socket = null;
        this.reconnectStrategy = ReconnectStrategy.Normal;
        this.connectTimer = null;
        this.reconnectTryCount = 0;
        this.reconnectDelayTimer = null;
        this._onClose = function (event) {
            if (_this.connectTimer) {
                clearTimeout(_this.connectTimer);
            }
            if (_this.socket) {
                _this._removeAllEventListeners();
                _this.socket = null;
            }
            _this.eventEmitter.emit('close', event);
            _this._reconnect();
        };
        this._onMessage = function (event) {
            _this.eventEmitter.emit('packet', utils_1.parse(event.data));
        };
        this._onError = function (error) {
            if (_this.connectTimer) {
                clearTimeout(_this.connectTimer);
            }
            if (_this.socket) {
                _this._removeAllEventListeners();
                _this.socket = null;
            }
            _this.eventEmitter.emit('error', error);
            _this._reconnect();
        };
        this._onOpen = function (event) {
            if (_this.connectTimer) {
                clearTimeout(_this.connectTimer);
                _this.connectTimer = null;
            }
            _this.eventEmitter.emit('open', event);
        };
        if (!userOption.url) {
            throw new Error('url not set');
        }
        Object.defineProperty(this, 'eventEmitter', {
            enumerable: false,
            configurable: false,
            value: new EventEmitter()
        });
        this.option = Object.assign({}, exports.baseOption, userOption);
    }
    BaseClient.prototype.connect = function () {
        var _this = this;
        this.eventEmitter.emit('connecting');
        this.socket = this.option.socketProvider.connect(this.option.url, this.option.proto);
        this.socket.addEventListener('close', this._onClose);
        this.socket.addEventListener('message', this._onMessage);
        this.socket.addEventListener('error', this._onError);
        this.socket.addEventListener('open', this._onOpen);
        this.connectTimer = setTimeout(function () {
            _this.socket && _this.socket.close(4020, 'connect timeout');
        }, this.option.connectTimeout);
        /**
         * Immediate only once
         */
        if (this.reconnectStrategy === ReconnectStrategy.Immediate) {
            this.reconnectStrategy = ReconnectStrategy.Normal;
        }
    };
    BaseClient.prototype.reconnect = function () {
        this.reconnectStrategy = ReconnectStrategy.Immediate;
        this.reconnectTryCount = 0;
        this._doClose();
    };
    BaseClient.prototype.close = function (shouldReconnect, code, reason) {
        if (shouldReconnect === void 0) { shouldReconnect = false; }
        if (code === void 0) { code = 1000; }
        if (reason === void 0) { reason = 'normal close'; }
        this.reconnectStrategy = shouldReconnect ? ReconnectStrategy.Normal : ReconnectStrategy.Stop;
        this._doClose(code, reason);
    };
    /**
     * raw send
     */
    BaseClient.prototype.send = function (data) {
        if (!this.socket) {
            throw new Error('websocket is null, call connect before send message');
        }
        else if (this.socket.readyState !== this.socket.OPEN) {
            throw new Error("websocket is not ready, state is '" + this.socket.readyState + "'");
        }
        else {
            // console.log('send', data);
            this.socket.send(utils_1.normalize(data));
        }
    };
    BaseClient.prototype._doClose = function (code, reason) {
        if (code === void 0) { code = 1000; }
        if (reason === void 0) { reason = 'normal close'; }
        if (!this.socket ||
            this.socket.readyState === this.socket.CLOSING ||
            this.socket.readyState === this.socket.CLOSED) {
            return;
        }
        this.socket.close(code, reason);
    };
    BaseClient.prototype._reconnect = function () {
        if (this.reconnectStrategy === ReconnectStrategy.Stop) {
            this.eventEmitter.emit('stop');
        }
        else if (this.option.maxRetryAttempts &&
            this.reconnectTryCount >= this.option.maxRetryAttempts) {
            this.eventEmitter.emit('error', 'Reconnect attempts more than maxRetryAttempts');
            this.eventEmitter.emit('stop');
        }
        else {
            this.eventEmitter.emit('reconnect');
            if (this.reconnectStrategy === ReconnectStrategy.Immediate) {
                this.connect();
            }
            else {
                this._delayedConnect();
            }
        }
    };
    BaseClient.prototype.getDelay = function (option) {
        var t = option.tryInterval * Math.pow(option.tryAccelerate, this.reconnectTryCount);
        return t > option.maxTryInterval ? option.maxTryInterval : t;
    };
    BaseClient.prototype._delayedConnect = function () {
        var _this = this;
        if (this.reconnectDelayTimer) {
            clearTimeout(this.reconnectDelayTimer);
            this.reconnectDelayTimer = null;
        }
        this.reconnectTryCount += 1;
        this.reconnectDelayTimer = setTimeout(function () {
            _this.connect();
        }, this.getDelay(this.option));
    };
    BaseClient.prototype._removeAllEventListeners = function () {
        if (!this.socket) {
            return;
        }
        this.socket.removeEventListener('close', this._onClose);
        this.socket.removeEventListener('message', this._onMessage);
        this.socket.removeEventListener('error', this._onError);
        this.socket.removeEventListener('open', this._onOpen);
    };
    BaseClient.prototype.on = function (event, listener, context) {
        this.eventEmitter.on(event, listener, context);
        return this;
    };
    BaseClient.prototype.addListener = function (event, listener, context) {
        this.eventEmitter.addListener(event, listener, context);
        return this;
    };
    BaseClient.prototype.once = function (event, listener, context) {
        this.eventEmitter.once(event, listener, context);
        return this;
    };
    BaseClient.prototype.off = function (event, listener, context, once) {
        this.eventEmitter.off(event, listener, context, once);
        return this;
    };
    BaseClient.prototype.removeListener = function (event, listener, context, once) {
        this.eventEmitter.removeListener(event, listener, context, once);
        return this;
    };
    BaseClient.prototype.removeAllListeners = function (event) {
        this.eventEmitter.removeAllListeners(event);
        return this;
    };
    return BaseClient;
}());
exports.BaseClient = BaseClient;
exports.default = BaseClient;
//# sourceMappingURL=Base.js.map